import React, { useState } from "react";
import { Button, Input, Message, Segment, Grid, Icon } from "semantic-ui-react";
import PropTypes from "prop-types";
import {
  validateISBN,
  fetchBookDataFromAPI,
  validateAPIConfig,
  mapCommonBookData,
  createInternalNotes,
  ISBNDB_API_KEY,
  ISBNDB_API_URL,
} from "@utils/isbnUtils";
import { searchForDocument } from "@utils/documentSearchUtils";

const MESSAGES = {
  ERROR: {
    EMPTY_ISBN: "Please enter an ISBN",
    INVALID_ISBN: "Please enter a valid ISBN (10 or 13 digits)",
    NO_API_KEY: "Please configure your ISBN API key in environment variables",
    FETCH_FAILED: "Failed to fetch book data",
    ISBN_NOT_FOUND: "ISBN not found",
    RATE_LIMIT: "API rate limit exceeded",
    NETWORK_ERROR: "Network error occurred",
  },
  SUCCESS: {
    WITH_DOCUMENT: " and linked to existing document",
    WITHOUT_DOCUMENT:
      ". No matching document found - you may need to catalog the document first",
  },
};

export const RJSFISBNLookupField = ({ uiSchema }) => {
  const [isbn, setIsbn] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const onDataFetched = uiSchema["ui:options"]?.onDataFetched;

  const mapISBNDataToItemForm = async (bookData) => {
    const mappedData = mapCommonBookData(bookData);

    if (bookData.title) {
      mappedData.description = bookData.title;
    }

    const isbnValue = bookData.isbn13 || bookData.isbn;
    if (isbnValue) {
      mappedData.isbns = [{ value: isbnValue, description: "Primary ISBN" }];
      mappedData.cover_metadata = { ISBN: isbnValue };
    }

    const documentPid = await searchForDocument(isbnValue, bookData.title);
    if (documentPid) {
      mappedData.document_pid = documentPid;
    }

    const internalNotes = createInternalNotes(bookData);
    if (internalNotes) {
      mappedData.internal_notes = internalNotes;
    }

    return mappedData;
  };

  const fetchISBNData = async () => {
    const trimmedIsbn = isbn.trim();

    if (!trimmedIsbn) {
      setError(MESSAGES.ERROR.EMPTY_ISBN);
      return;
    }

    if (!validateISBN(trimmedIsbn)) {
      setError(MESSAGES.ERROR.INVALID_ISBN);
      return;
    }

    if (!validateAPIConfig(ISBNDB_API_KEY)) {
      setError(MESSAGES.ERROR.NO_API_KEY);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const bookData = await fetchBookDataFromAPI(
        trimmedIsbn,
        ISBNDB_API_KEY,
        ISBNDB_API_URL
      );

      // Check if bookData is null/undefined (ISBN not found in API)
      if (!bookData) {
        setError(
          `ISBN "${trimmedIsbn}" not found. Please verify the ISBN or enter item details manually.`
        );
        setIsLoading(false);
        return;
      }

      const mappedData = await mapISBNDataToItemForm(bookData);

      if (onDataFetched) {
        onDataFetched(mappedData);
      }

      const documentMessage = mappedData.document_pid
        ? MESSAGES.SUCCESS.WITH_DOCUMENT
        : MESSAGES.SUCCESS.WITHOUT_DOCUMENT;

      setSuccessMessage(
        `Book data successfully imported${documentMessage}. Please review and complete the required fields.`
      );
      setIsbn("");
    } catch (error) {
      // Handle different types of errors
      if (error.message && error.message.includes("not found")) {
        setError(
          `ISBN "${trimmedIsbn}" not found. Please verify the ISBN or enter item details manually.`
        );
      } else if (error.message && error.message.includes("rate limit")) {
        setError(
          "API rate limit exceeded. Please wait a moment and try again."
        );
      } else {
        setError(
          "Failed to fetch book data. Please check your internet connection and try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      fetchISBNData();
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const clearInput = () => {
    setIsbn("");
    clearMessages();
  };

  const handleInputChange = (e) => {
    setIsbn(e.target.value);
    clearMessages();
  };

  return (
    <Segment
      style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <h4 style={{ margin: "0 0 10px 0", color: "#225745" }}>
              <Icon name="book" />
              ISBN Lookup
            </h4>
            <strong
              style={{ color: "#666", fontSize: "14px", margin: "0 0 15px 0" }}
            >
              Enter an ISBN to automatically populate item information.
            </strong>
            <p
              style={{
                color: "#666",
                fontSize: "14px",
                margin: "5px 0 15px 0",
              }}
            >
              <i>Other fields must be filled in manually.</i>
            </p>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={12}>
            <Input
              fluid
              placeholder="Enter ISBN (10 or 13 digits, e.g., 9780123456789)"
              style={{ marginTop: "-15px" }}
              value={isbn}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              icon={
                isbn
                  ? {
                      name: "remove",
                      circular: true,
                      link: true,
                      onClick: clearInput,
                    }
                  : null
              }
            />
            <p
              style={{
                color: "red",
                fontSize: "13px",
                margin: "10px 0 15px 5px",
              }}
            >
              This is optional - you can also manually fill all fields.
            </p>
          </Grid.Column>

          <Grid.Column width={4}>
            <Button
              primary
              fluid
              loading={isLoading}
              onClick={fetchISBNData}
              disabled={!isbn.trim() || isLoading}
              style={{ marginTop: "-15px" }}
            >
              <Icon name="search" />
              {isLoading ? "Loading..." : "Lookup"}
            </Button>
          </Grid.Column>
        </Grid.Row>

        {error && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message negative onDismiss={clearMessages}>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        {successMessage && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message positive onDismiss={clearMessages}>
                <Message.Header>Success</Message.Header>
                <p>{successMessage}</p>
                <p
                  style={{ fontSize: "13px", marginTop: "10px", color: "#666" }}
                >
                  <strong>Note:</strong> Please complete the required fields
                  (barcode, status, location, shelf, rack). If no document was
                  found and linked automatically, you may need to catalog the
                  document first before creating the item.
                </p>
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Segment>
  );
};

RJSFISBNLookupField.propTypes = {
  uiSchema: PropTypes.object.isRequired,
};

export default RJSFISBNLookupField;
